"use client"

import { useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useTheme } from "../contexts/ThemeContext"

function Navbar() {
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate("/login")
  }

  const isActive = (path) => {
    return location.pathname === path
  }

  return (
    <nav className={theme === "dark" ? "bg-black/90 backdrop-blur-md shadow-xl border-b border-gray-800 sticky top-0 z-50" : "bg-white/80 backdrop-blur-md shadow-xl border-b border-indigo-100 sticky top-0 z-50"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center group">
              <div className={theme === "dark" ? "w-10 h-10 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform" : "w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform"}>
                <img src="/brand-logo.svg" alt="Logo" className="w-9 h-9" />
              </div>
              <span className={theme === "dark" ? "ml-3 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-100 via-gray-400 to-gray-700 drop-shadow" : "ml-3 text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 drop-shadow"}>Inflatein AI Studio</span>
            </Link>
          </div>
          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Theme Switcher for Desktop */}
            <button
              onClick={toggleTheme}
              className={theme === "dark" ? "px-3 py-2 rounded-lg text-base font-semibold bg-gray-800 text-white border border-gray-700 shadow hover:bg-gray-700 transition-colors" : "px-3 py-2 rounded-lg text-base font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 shadow hover:bg-indigo-100 transition-colors"}
            >
              {theme === "dark" ? "🌙 Dark" : "☀️ Light"}
            </button>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/dashboard")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-indigo-600 bg-indigo-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/subscription"
                  className={`px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/subscription")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-pink-600 bg-pink-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-pink-600 hover:bg-pink-50"
                  }`}
                >
                  Subscription
                </Link>
                <div className="flex items-center space-x-4 ml-4">
                  <div className="flex items-center space-x-2">
                    <div className={theme === "dark" ? "w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center border border-gray-700 shadow" : "w-9 h-9 bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 rounded-full flex items-center justify-center border border-indigo-200 shadow"}>
                      <span className={theme === "dark" ? "text-lg font-bold text-gray-200" : "text-lg font-bold text-indigo-700"}>
                        {user.email ? user.email.charAt(0).toUpperCase() : "U"}
                      </span>
                    </div>
                    <span className={theme === "dark" ? "text-base text-gray-300 hidden lg:block font-medium" : "text-base text-gray-700 hidden lg:block font-medium"}>
                      {user.email}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={theme === "dark" ? "px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-200 border border-gray-700 shadow" : "px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 text-indigo-700 border border-indigo-200 shadow"}>
                      {typeof user.credit_balance === "number" ? user.credit_balance : (user.credit_balance || 0)} credits
                    </span>
                    {user.subscription_type && (
                      <span className={theme === "dark" ? "px-2 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-200 border border-gray-700 shadow capitalize" : "px-2 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-pink-100 via-purple-100 to-indigo-100 text-pink-700 border border-pink-200 shadow capitalize"}>
                        {user.subscription_type}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleLogout}
                    className={theme === "dark" ? "px-4 py-2 text-base font-semibold text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors shadow" : "px-4 py-2 text-base font-semibold text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-indigo-500 hover:to-pink-500 rounded-lg transition-colors shadow"}
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  onClick={e => {
                    if (location.pathname === "/") {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                  className={`px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-indigo-600 bg-indigo-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  Home
                </Link>
                <a
                  href="#pricing"
                  onClick={e => {
                    e.preventDefault();
                    if (location.pathname !== "/") {
                      navigate("/", { replace: false });
                      setTimeout(() => {
                        const pricingSection = document.getElementById("pricing");
                        if (pricingSection) {
                          pricingSection.scrollIntoView({ behavior: "smooth" });
                        }
                      }, 300); // Wait for navigation/render
                    } else {
                      const pricingSection = document.getElementById("pricing");
                      if (pricingSection) {
                        pricingSection.scrollIntoView({ behavior: "smooth" });
                      }
                    }
                  }}
                  className={theme === "dark" ? "px-3 py-2 rounded-lg text-base font-semibold transition-colors text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "px-3 py-2 rounded-lg text-base font-semibold transition-colors text-gray-700 hover:text-pink-600 hover:bg-pink-50"}
                >
                  Pricing
                </a>
                <Link
                  to="/login"
                  className={`px-4 py-2 text-base font-semibold transition-colors ${
                    isActive("/login")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-indigo-600 bg-indigo-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className={theme === "dark" ? "px-4 py-2 text-base font-semibold text-white bg-gray-800 hover:bg-gray-700 rounded-lg shadow transition-colors" : "px-4 py-2 text-base font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:to-pink-600 rounded-lg shadow transition-colors"}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={theme === "dark" ? "inline-flex items-center justify-center p-2 rounded-md text-gray-300 hover:text-gray-100 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500" : "inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-indigo-600 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"}
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className={theme === "dark" ? "px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-black border-t border-gray-700 shadow-xl" : "px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white/90 border-t border-indigo-100 shadow-xl"}>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`block px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/dashboard")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-indigo-600 bg-indigo-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link
                  to="/subscription"
                  className={`block px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/subscription")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-pink-600 bg-pink-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-pink-600 hover:bg-pink-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Subscription
                </Link>
                <div className={theme === "dark" ? "border-t border-gray-700 pt-4 pb-3" : "border-t border-indigo-100 pt-4 pb-3"}>
                  <div className="flex items-center px-3">
                    <div className={theme === "dark" ? "w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center border border-gray-700 shadow" : "w-9 h-9 bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 rounded-full flex items-center justify-center border border-indigo-200 shadow"}>
                      <span className={theme === "dark" ? "text-lg font-bold text-gray-200" : "text-lg font-bold text-indigo-700"}>
                        {user.email ? user.email.charAt(0).toUpperCase() : "U"}
                      </span>
                    </div>
                    <div className="ml-3">
                      <div className={theme === "dark" ? "text-base font-semibold text-gray-200" : "text-base font-semibold text-gray-800"}>{user.email}</div>
                      <div className={theme === "dark" ? "text-sm text-gray-400 font-medium" : "text-sm text-indigo-700 font-medium"}>{typeof user.credit_balance === "number" ? user.credit_balance : (user.credit_balance || 0)} credits</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsMenuOpen(false)
                      }}
                      className={theme === "dark" ? "block w-full text-left px-3 py-2 text-base font-semibold text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors shadow" : "block w-full text-left px-3 py-2 text-base font-semibold text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-indigo-500 hover:to-pink-500 rounded-lg transition-colors shadow"}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  onClick={e => {
                    if (location.pathname === "/") {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                    setIsMenuOpen(false);
                  }}
                  className={`block px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-indigo-600 bg-indigo-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                >
                  Home
                </Link>
                <a
                  href="#pricing"
                  onClick={e => {
                    e.preventDefault();
                    if (location.pathname !== "/") {
                      navigate("/", { replace: false });
                      setTimeout(() => {
                        const pricingSection = document.getElementById("pricing");
                        if (pricingSection) {
                          pricingSection.scrollIntoView({ behavior: "smooth" });
                        }
                      }, 300);
                      setIsMenuOpen(false);
                    } else {
                      const pricingSection = document.getElementById("pricing");
                      if (pricingSection) {
                        pricingSection.scrollIntoView({ behavior: "smooth" });
                      }
                      setIsMenuOpen(false);
                    }
                  }}
                  className={theme === "dark" ? "block px-3 py-2 rounded-lg text-base font-semibold transition-colors text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "block px-3 py-2 rounded-lg text-base font-semibold transition-colors text-gray-700 hover:text-pink-600 hover:bg-pink-50"}
                >
                  Pricing
                </a>
                <Link
                  to="/login"
                  className={`block px-3 py-2 rounded-lg text-base font-semibold transition-colors ${
                    isActive("/login")
                      ? theme === "dark" ? "text-gray-100 bg-gray-800 shadow" : "text-indigo-600 bg-indigo-50 shadow"
                      : theme === "dark" ? "text-gray-300 hover:text-gray-100 hover:bg-gray-800" : "text-gray-700 hover:text-indigo-600 hover:bg-indigo-50"
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className={theme === "dark" ? "block px-3 py-2 rounded-lg text-base font-semibold text-white bg-gray-800 hover:bg-gray-700 shadow transition-colors" : "block px-3 py-2 rounded-lg text-base font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:to-pink-600 shadow transition-colors"}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Sign Up
                </Link>
                {/* Theme Switcher for Mobile */}
                <div className={theme === "dark" ? "border-t border-gray-700 pt-4 pb-3" : "border-t border-indigo-100 pt-4 pb-3"}>
                  <button
                    onClick={() => {
                      toggleTheme();
                      setIsMenuOpen(false);
                    }}
                    className={theme === "dark" ? "block w-full text-left px-3 py-2 text-base font-semibold bg-gray-800 text-white border border-gray-700 rounded-lg shadow hover:bg-gray-700 transition-colors" : "block w-full text-left px-3 py-2 text-base font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg shadow hover:bg-indigo-100 transition-colors"}
                  >
                    {theme === "dark" ? "🌙 Dark Mode" : "☀️ Light Mode"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar